import React, { useMemo } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useObservable } from "micro-observables";
import styled from "@emotion/styled";
import { ConditionalSkeleton } from "@ui/components/common/conditional-skeleton";
import { useExercisePage } from "@ui/pages/exercise/hooks/use-exercise-page";
import { exerciseFilterManager } from "@core/service-container";
import { exerciseManager } from "@core/service-container";

interface ExerciseCountsProps {
	loading: boolean;
}

const DUMMY_EXERCISE_COUNT = 99;

export const ExerciseCounts: React.FC<ExerciseCountsProps> = ({ loading }) => {
	const { format } = useI18n();
	const { filteredExercises, search } = useExercisePage();
	const exerciseTotalCount = useObservable(exerciseManager.exerciseTotalCount);

	const activeFilters = useObservable(exerciseFilterManager.activeFilters);

	const isAnyFilterActive = useMemo(() => activeFilters.length > 0 || search, [activeFilters.length, search]);

	const content = useMemo(() => {
		if (loading) {
			return format("exerciseListing.exerciseCount", { count: DUMMY_EXERCISE_COUNT });
		}

		return isAnyFilterActive
			? format("exerciseListing.filteredExerciseCounts", {
					filteredCount: filteredExercises.length,
					totalCount: exerciseTotalCount,
				})
			: format("exerciseListing.exerciseCount", { count: exerciseTotalCount });
	}, [exerciseTotalCount, filteredExercises.length, format, isAnyFilterActive, loading]);

	return (
		<ConditionalSkeleton contentDriven={true} active={loading} loading={loading}>
			<Container>{content}</Container>
		</ConditionalSkeleton>
	);
};

const Container = styled.div`
	white-space: nowrap;
`;
